import { circleOccupancy, resolvePlacement, type FtMoveShape } from '../../../../ft3d/engine/ft3d-move-core.js';
import { encounterCreatureField, inventoryField, itemsCol } from '../../shared/array-types.js';
import { resolveFirstUnobstructedPosition, type MoveDestination } from './move-destination.js';
import type { PlainWalkReaders } from './plain-walk-scope.js';
import type { PlainWalkResult, WalkPoint } from './plain-walk.js';
import { movementFootprint, movementOverlap, movementRadius, type MovementBody } from './body-geometry.js';

type Row = Record<string, unknown>;
type Prop = { row: Row; inventory: string | null; x: number; y: number; reach: number };
type Disc = ReturnType<typeof circleOccupancy> & { r: number; body?: MovementBody };
export type PropField = { props: Prop[] } | { why: 'prop-position' | 'prop-footprint-unknown'; inventory: string | null };
const MAX_SNAP_FT = 40;
// Keep the persisted 3-dp point strictly outside the original shapeless reach's inclusive boundary.
const EPS = 0.002;
export const OPEN_PLANE: FtMoveShape = { v: 1, unit: 'ft', boxes: [{ id: 'unshaped', x: { min: -1e6, max: 1e6 }, y: { min: -1e6, max: 1e6 }, floor: 0, ceiling: 1e6 }], openings: [] };
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const round3 = (n: number): number => Number(n.toFixed(3));

/** Read each footprint once, before placement searches. Cheap size bounds exclude distant props first.
 * Unbounded (scaled / unsized) footprints must still be measured: guessing a bound could miss a huge mesh,
 * and an unmeasurable prop retains its existing named refusal, even when distant. */
export function shapelessPropField(inventories: Row[], at: WalkPoint, moverRadius: number, read: PlainWalkReaders, searchFt = MAX_SNAP_FT): PropField {
  const props: Prop[] = [];
  for (const row of inventories) {
    if (Number(inventoryField(row, 'inventory_deleted') ?? 0) === 1) continue;
    const inventory = inventoryField<string>(row, 'inventory_id') ?? null;
    const [x, y] = read.position(row);
    if (![x, y].every(Number.isFinite)) return { why: 'prop-position', inventory };
    const distance = Math.hypot(at.x - x, at.y - y);
    const g = read.geometry(row);
    const transform = record(inventoryField(row, 'transform_3d'));
    let reach = g && Number.isFinite(g.diameter) && g.diameter > 0 && !(transform && 'scaleXYZ' in transform) ? g.diameter * Math.SQRT1_2 : null;
    if (reach !== null && distance > searchFt + reach + moverRadius + 0.5 + EPS) continue;
    if (reach === null) {
      const footprint = read.propFootprint?.(row) ?? null;
      const span = footprint?.kind === 'circle' ? footprint.r * Math.SQRT2 : footprint ? Math.hypot(footprint.halfW, footprint.halfD) : NaN;
      if (!Number.isFinite(span) || span < 0) return { why: 'prop-footprint-unknown', inventory };
      reach = span;
    }
    if (distance <= searchFt + reach + moverRadius + 0.5 + EPS) props.push({ row, inventory, x, y, reach });
  }
  return { props };
}

export function shapelessPropAt(field: { props: Prop[] }, at: WalkPoint, radius: number): Prop | undefined {
  return field.props.find(p => Math.hypot(at.x - p.x, at.y - p.y) <= p.reach + radius + 0.5);
}

export function propSnapInfo(prop: Prop, requested: WalkPoint, stop: WalkPoint, read: PlainWalkReaders): NonNullable<PlainWalkResult['snapped']> {
  const definition = read.definition(inventoryField<string>(prop.row, 'inventory_item_id') ?? '');
  const name = inventoryField<string>(prop.row, 'inventory_name') || itemsCol<string>(definition, 'item_name') || 'the prop';
  return { requested: { ...requested }, inventory: prop.inventory, distance_ft: Math.hypot(stop.x - requested.x, stop.y - requested.y),
    message: `Moved to the nearest free spot beside ${name === 'the prop' ? name : 'the ' + name}.` };
}

/** Same living-body discs and height band as crossedBody; sparse dead/private flags read as zero. */
export function shapelessStandingBodies(creatures: Row[], actorId: string, mover: { diameter: number; height: number }, read: PlainWalkReaders): Disc[] {
  return creatures.flatMap(row => {
    const ec = record(row.encounter_creatures) ?? row;
    const id = String(encounterCreatureField(ec, 'encounter_creature_id') ?? '');
    if (id.toUpperCase() === actorId.toUpperCase() || Number(encounterCreatureField(ec, 'encounter_creature_dead') ?? 0) === 1
      || Number(encounterCreatureField(ec, 'encounter_creature_private') ?? 0) === 1) return [];
    const g = read.geometry(row), [x, y, z] = read.position(row);
    if (!g || ![x, y, z, g.diameter, g.height].every(Number.isFinite) || z >= mover.height - 1e-6 || z + g.height <= 1e-6) return [];
    return [{ ...movementFootprint(g, x, y, 0), r: movementRadius(g), body: g }];
  });
}

export function snapBesideProp(field: { props: Prop[] }, requested: WalkPoint, radius: number,
  bodies: Disc[], body: MovementBody = { diameter: radius * 2, height: 1 }): WalkPoint | null {
  const obstacles = [...field.props.map(p => circleOccupancy(p.x, p.y, p.reach + 0.5 + EPS, 0, 1)), ...bodies];
  const resolve = (point: WalkPoint): MoveDestination => {
    const placed = resolvePlacement(OPEN_PLANE, movementFootprint(body, point.x, point.y, 0), obstacles);
    if (placed.status !== 'placed') return { status: 'blocked', reason: placed.reason ?? 'wedged' };
    const stop = { x: round3(placed.x), y: round3(placed.y), z: 0 };
    if (Math.hypot(stop.x - requested.x, stop.y - requested.y) > MAX_SNAP_FT || shapelessPropAt(field, stop, radius)
      || bodies.some(b => movementOverlap(body, stop, b.body ?? { diameter: b.r * 2, height: 1 }, { x: b.x, y: b.y, z: 0 }))) return { status: 'blocked', reason: 'wedged' };
    return { status: 'placed', ...stop, moved: stop.x !== requested.x || stop.y !== requested.y };
  };
  let placed = resolve(requested);
  if (placed.status === 'blocked' && placed.reason === 'wedged') placed = resolveFirstUnobstructedPosition(requested, radius * 2, resolve, MAX_SNAP_FT);
  return placed.status === 'placed' ? { x: placed.x, y: placed.y, z: 0 } : null;
}
